import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'Which cost-basis method does the IRS use if I never pick one?',
    a: 'FIFO — First-In, First-Out. If you (or your broker) don&apos;t specify a method before the trade settles, the IRS assumes your oldest shares were sold first. Robinhood goes a step further and <strong>only</strong> supports FIFO, with no override. Schwab, Fidelity, Vanguard, and E*TRADE all let you set a different default or pick lots per-trade.',
  },
  {
    q: 'Can I change the cost-basis method after the sale settles?',
    a: 'No. Lot identification must happen <strong>at or before settlement</strong> (typically T+1). Once the trade settles under one method, that&apos;s your tax reality for the year — the 1099-B your broker files with the IRS reflects it. This is exactly why previewing the tax consequence before you click sell matters so much.',
  },
  {
    q: 'Does a DRIP reinvestment really trigger a wash sale?',
    a: 'Yes. A dividend reinvestment is a purchase in the eyes of the IRS. If your DRIP buys even a fraction of a share within 30 days before or after you sell that ticker at a loss, the loss attributable to those replacement shares is disallowed (a partial wash sale). When harvesting a loss, turn DRIP off for the ticker and wait until Day 31 to re-enable it.',
  },
  {
    q: 'Is selling VOO and immediately buying SPY a wash sale?',
    a: 'Officially unsettled — the IRS has never published a bright-line rule for ETFs — but VOO, SPY, and IVV all track the identical S&amp;P 500 index, so most tax professionals treat that swap as high-risk for the &quot;substantially identical&quot; test. The safe playbook: swap into a fund tracking a <strong>different</strong> index (VOO → VTI tracks total-market, VOO → QQQ tracks Nasdaq-100) or a direct competitor stock (F → GM), then swap back after Day 31 if you want the original exposure.',
  },
  {
    q: 'Which method should a dividend investor actually use?',
    a: 'For most sales, the Tax Lot Optimizer produces the lowest current-year tax bill: it consumes short-term losses first (they offset ordinary income), then long-term losses, then long-term gains (preferential 0/15/20% rate), and saves short-term gains for last. Use HIFO when you specifically want the maximum harvestable loss today, and Low Cost (LOFO) when you&apos;re intentionally realizing gains in the 0% long-term bracket. If your real broker is Robinhood, pick FIFO — that&apos;s what Robinhood will execute regardless of your preference.',
  },
];

export default function LearnCostBasisMethods() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q.replace(/&quot;/g, '"').replace(/&apos;/g, "'"),
      acceptedAnswer: {
        '@type': 'Answer',
        text: a.replace(/<[^>]+>/g, '').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&'),
      },
    })),
  };

  return (
    <LearnPage
      title="FIFO vs LIFO vs HIFO vs Tax Lot Optimizer — Choosing Your Cost-Basis Method (2026) | DiviDrip"
      description="The broker setting that quietly controls your tax bill: how FIFO, LIFO, HIFO, Low Cost, and the Tax Lot Optimizer pick which shares you sell, worked examples at three price points, wash-sale traps (DRIP included), and the substantially-identical ETF substitution guide."
      canonical="https://twylightcrow.com/learn/cost-basis-methods-fifo-lifo-hifo"
      guidePath="/learn/cost-basis-methods-fifo-lifo-hifo"
      sectionLabel="Dividend Taxes"
      sectionAnchor="tax"
      kicker="Learn · Tax strategy"
      h1="Choosing Your Cost-Basis Method — FIFO vs LIFO vs HIFO vs the Tax Lot Optimizer"
      datePublished="2026-06-11"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — the rate that gets applied' },
        { to: '/learn/tax-loss-harvesting-playbook', label: 'The tax-loss harvesting playbook' },
        { to: '/learn/niit-surtax', label: 'Navigating the NIIT — the 3.8% surtax guide' },
        { to: '/learn/foreign-ordinary-shares-tax', label: 'Foreign ordinary shares & the Foreign Tax Credit' },
        { to: '/glossary', label: 'Glossary — FIFO, HIFO, Wash Sale, and every term used here' },
      ]}
    >
      <p>
        Buried in your brokerage settings is a default most investors have never
        looked at — and it decides, every single time you sell, <em>which</em> of
        your shares get sold and therefore how big the taxable gain (or harvestable
        loss) is. Two investors can sell the identical number of shares of the
        identical stock on the identical day and report wildly different tax
        outcomes, purely because of the <strong>cost-basis method</strong> their
        broker applied. This guide covers all five methods, when each one wins,
        and the wash-sale traps that can silently undo your tax planning.
      </p>

      <h2>The five methods at a glance</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Method</th>
            <th className="text-left font-semibold">What it sells first</th>
            <th className="text-left font-semibold">Ideal use case</th>
            <th className="text-left font-semibold">The catch</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">FIFO<br /><span className="font-normal text-xs">First-In, First-Out</span></td>
            <td>Oldest tax lots</td>
            <td>Default / hands-off. Most likely to land long-term rates.</td>
            <td>Oldest shares usually have the lowest basis → biggest gain in an appreciated stock.</td>
          </tr>
          <tr>
            <td className="font-semibold">LIFO<br /><span className="font-normal text-xs">Last-In, First-Out</span></td>
            <td>Newest tax lots</td>
            <td>Rising market — newest shares show the smallest paper gain.</td>
            <td>Newest shares are almost always short-term → ordinary rates up to 37%.</td>
          </tr>
          <tr>
            <td className="font-semibold">HIFO<br /><span className="font-normal text-xs">Highest-In, First-Out</span></td>
            <td>Most expensive lots</td>
            <td>Tax-loss harvesting — maximizes losses / minimizes gains this year.</td>
            <td>Ignores the calendar; can grab short-term lots.</td>
          </tr>
          <tr>
            <td className="font-semibold">Low Cost (LOFO)<br /><span className="font-normal text-xs">Lowest-In, First-Out</span></td>
            <td>Cheapest lots</td>
            <td>Tax-<em>gain</em> harvesting in the 0% LTCG bracket; charitable planning; burning off loss carryovers.</td>
            <td>Realizes the biggest gain — only smart in specific situations.</td>
          </tr>
          <tr>
            <td className="font-semibold">Tax Lot Optimizer</td>
            <td>Most tax-favorable lots, 4-bucket priority</td>
            <td>The lowest current-year tax bill on most sales.</td>
            <td>Optimizes <em>this</em> year — can defer bigger gains into future years.</td>
          </tr>
        </tbody>
      </table>
      <p>
        The Tax Lot Optimizer&apos;s priority order (popularized by Schwab) is worth
        memorizing: <strong>(1)</strong> short-term losses, largest first — they
        offset ordinary income; <strong>(2)</strong> long-term losses, largest
        first; <strong>(3)</strong> long-term gains, smallest first — taxed at the
        preferential 0/15/20% rate; <strong>(4)</strong> short-term gains, smallest
        first — taxed at your full ordinary rate, so they go last.
      </p>

      <h2>What your broker actually lets you pick</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Broker</th>
            <th className="text-left font-semibold">Default</th>
            <th className="text-left font-semibold">Can you change it?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Robinhood</td>
            <td>FIFO</td>
            <td><strong>No.</strong> FIFO only — no account default change, no per-trade lot picking.</td>
          </tr>
          <tr>
            <td className="font-semibold">Schwab</td>
            <td>FIFO</td>
            <td>Yes — account-level default (incl. Tax Lot Optimizer) + per-trade specific-lot selection.</td>
          </tr>
          <tr>
            <td className="font-semibold">Fidelity</td>
            <td>FIFO</td>
            <td>Yes — account default + per-trade lot picking, incl. a &quot;Tax-Sensitive&quot; smart mode.</td>
          </tr>
          <tr>
            <td className="font-semibold">Vanguard</td>
            <td>FIFO (avg cost on mutual funds)</td>
            <td>Yes — SpecID unlocks per-lot control on ETFs and stocks.</td>
          </tr>
          <tr>
            <td className="font-semibold">E*TRADE</td>
            <td>FIFO</td>
            <td>Yes — account default + per-trade selection.</td>
          </tr>
        </tbody>
      </table>
      <p>
        The Robinhood row is the punchline:{' '}
        <strong>
          Robinhood always sells your oldest shares first and gives you no say in
          it.
        </strong>{' '}
        DiviDrip&apos;s Multi-Share Sell widget shows you what all five methods{' '}
        <em>would</em> do before you sell — so even Robinhood users can see the
        FIFO tax consequence in advance, and multi-broker users can pick the lot
        strategy their real broker supports.
      </p>

      <h2>Worked example — same sale, five different tax bills</h2>
      <p>
        Say you own 40 shares of a stock across four tax lots, and you sell 10
        shares today:
      </p>
      <ul>
        <li><strong>Lot A:</strong> 10 shares, bought 2 years ago at $40 (long-term)</li>
        <li><strong>Lot B:</strong> 10 shares, bought 2 months ago at $60 (short-term)</li>
        <li><strong>Lot C:</strong> 10 shares, bought 3 years ago at $120 (long-term)</li>
        <li><strong>Lot D:</strong> 10 shares, bought 5 months ago at $150 (short-term)</li>
      </ul>

      <h3>Scenario 1 — stock at $100 (mixed gains and losses)</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Method</th>
            <th className="text-left font-semibold">Lot sold</th>
            <th className="text-left font-semibold">Result</th>
            <th className="text-left font-semibold">Tax character</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>FIFO</td><td>Lot C (oldest)</td><td>−$200 loss</td><td>Long-term loss</td></tr>
          <tr><td>LIFO</td><td>Lot B (newest)</td><td>+$400 gain</td><td>Short-term gain (ordinary rate)</td></tr>
          <tr><td>HIFO</td><td>Lot D ($150 basis)</td><td><strong>−$500 loss</strong></td><td>Short-term loss (max deduction power)</td></tr>
          <tr><td>Low Cost</td><td>Lot A ($40 basis)</td><td>+$600 gain</td><td>Long-term gain</td></tr>
          <tr><td>Optimizer</td><td>Lot D (ST loss bucket)</td><td><strong>−$500 loss</strong></td><td>Short-term loss</td></tr>
        </tbody>
      </table>

      <h3>Scenario 2 — stock skyrockets to $200 (every lot profitable)</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Method</th>
            <th className="text-left font-semibold">Lot sold</th>
            <th className="text-left font-semibold">Gain</th>
            <th className="text-left font-semibold">Approx. tax (15% LT / 24% ST)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Optimizer</td><td>Lot C (smallest LT gain)</td><td>+$800</td><td><strong>$120</strong></td></tr>
          <tr><td>FIFO</td><td>Lot C (oldest)</td><td>+$800</td><td><strong>$120</strong></td></tr>
          <tr><td>HIFO</td><td>Lot D</td><td>+$500</td><td>$120 (smaller gain, but short-term rate)</td></tr>
          <tr><td>LIFO</td><td>Lot B</td><td>+$1,400</td><td>$336 — the worst pick by far</td></tr>
        </tbody>
      </table>
      <p>
        Note the HIFO subtlety: the <em>smallest dollar gain</em> ($500) does not
        produce the smallest tax bill, because it&apos;s a short-term gain taxed at
        ordinary rates. Holding-period character matters as much as the dollar
        amount — this is exactly the trade-off the Optimizer&apos;s bucket order
        encodes.
      </p>

      <h3>Scenario 3 — stock crashes to $30 (harvesting the loss)</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Method</th>
            <th className="text-left font-semibold">Lot sold</th>
            <th className="text-left font-semibold">Loss harvested</th>
            <th className="text-left font-semibold">Deduction power</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>HIFO / Optimizer</td><td>Lot D</td><td><strong>−$1,200 short-term</strong></td><td>Max — ST losses offset ordinary income first</td></tr>
          <tr><td>FIFO</td><td>Lot C</td><td>−$900 long-term</td><td>Moderate</td></tr>
          <tr><td>LIFO</td><td>Lot B</td><td>−$300 short-term</td><td>Minimal</td></tr>
        </tbody>
      </table>
      <p>
        Harvested losses offset capital gains dollar-for-dollar, and up to{' '}
        <strong>$3,000 per year</strong> of leftover loss deducts against ordinary
        income (the rest carries forward indefinitely). See the full{' '}
        <Link to="/learn/tax-loss-harvesting-playbook">tax-loss harvesting playbook</Link>.
      </p>

      <h2>The wash-sale rule — three traps that undo your harvest</h2>
      <p>
        Selling at a loss only produces a deduction if you avoid a{' '}
        <strong>wash sale</strong>: buying the same (or a substantially identical)
        security within the <strong>61-day danger window</strong> — 30 days before
        the sale, the sale day itself, and 30 days after. Trip the rule and the
        loss is disallowed and rolled into the new shares&apos; basis (deferred) —
        or <em>permanently destroyed</em> if the repurchase happened inside an IRA.
      </p>
      <ol>
        <li>
          <strong>Trap 1 — the quick re-entry.</strong> You harvest a loss, the
          stock dips further three days later, and you &quot;buy the dip.&quot;
          Wash sale. The deduction is gone for this year.
        </li>
        <li>
          <strong>Trap 2 — DRIP.</strong> Your dividend reinvestment plan buys a
          fractional share inside the window. That counts as a purchase and
          triggers a partial wash sale. Turn DRIP off on the ticker before
          harvesting.
        </li>
        <li>
          <strong>Trap 3 — the other account.</strong> The rule applies across{' '}
          <em>all</em> your accounts — a different broker, your IRA, even your
          spouse&apos;s account. Selling in taxable and re-buying in the Roth is
          the worst version: the loss is disallowed <em>and</em> can never be
          recovered through basis adjustment.
        </li>
      </ol>

      <h2>&quot;Substantially identical&quot; — the ETF substitution guide</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Swap</th>
            <th className="text-left font-semibold">Wash-sale risk</th>
            <th className="text-left font-semibold">Why</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Same ticker, any account</td><td>Definite</td><td>Identical security.</td></tr>
          <tr><td>GOOGL ↔ GOOG, BRK.A ↔ BRK.B</td><td>Definite</td><td>Different share class, same company.</td></tr>
          <tr><td>Stock ↔ options on that stock</td><td>Definite</td><td>Options count as acquiring the position.</td></tr>
          <tr><td>VOO ↔ SPY ↔ IVV</td><td>High</td><td>Same S&amp;P 500 index — most pros treat these as substantially identical.</td></tr>
          <tr><td>VOO → VTI</td><td>Generally safe</td><td>S&amp;P 500 vs total-market — different index, ~13% different holdings weight.</td></tr>
          <tr><td>VOO → QQQ or DIA</td><td>Safe</td><td>Different index entirely.</td></tr>
          <tr><td>F → GM</td><td>Safe</td><td>Direct competitor, different issuer.</td></tr>
        </tbody>
      </table>

      <h3>The 31-day safe-swap-back timeline</h3>
      <ul>
        <li><strong>Day 0:</strong> sell the losing lot. Make sure no purchase of the same ticker happened in the prior 30 days (DRIP included).</li>
        <li><strong>Days 1–30:</strong> the danger window. Hold cash or a non-identical substitute. Keep DRIP off.</li>
        <li><strong>Day 31:</strong> safe to repurchase the original ticker. Calendar days, not business days — if Day 31 is a weekend or holiday, wait for the next market open.</li>
      </ul>

      <h2>Using the Multi-Share Sell widget in DiviDrip</h2>
      <p>
        DiviDrip tracks every buy as its own tax lot — which means a position
        built through years of DRIPs and partial buys can hold dozens of lots
        with different bases and holding periods. The{' '}
        <strong>Multi-Share Sell</strong> widget turns &quot;sell 15 shares across
        9 lots&quot; from a manual fraction-counting exercise into one click:
      </p>
      <ol>
        <li>
          On your Portfolio page, expand any ticker row (or open the Stock Modal
          → <strong>My Portfolio</strong> tab) and click{' '}
          <strong>Sell Multiple Shares</strong> at the top of the lot list.
        </li>
        <li>
          Enter the share count and pick a method —{' '}
          <AbbrTip term="FIFO">FIFO</AbbrTip> is pre-checked because it&apos;s the
          IRS default and Robinhood&apos;s only behavior. Click any method name
          for its definition.
        </li>
        <li>
          Review the live preview: every lot that will be consumed, in order,
          with the estimated gain/loss split between short-term and long-term —
          plus a <AbbrTip term="WASH_SALE">wash-sale</AbbrTip> warning whenever
          the sale realizes a loss. Then click <strong>Execute sell</strong>.
        </li>
      </ol>
      <p>
        DiviDrip is a tracker, not a broker — the widget updates your DiviDrip
        records and cash balance, and you place the matching trade with your real
        brokerage.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt
              className="font-semibold text-foreground"
              dangerouslySetInnerHTML={{ __html: q }}
            />
            <dd
              className="mt-1 text-foreground/85"
              dangerouslySetInnerHTML={{ __html: a }}
            />
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not tax advice. Cost-basis elections, wash-sale
        determinations, and the &quot;substantially identical&quot; test depend on
        your full personal situation, and broker execution can differ from the
        typical cases described here. Confirm specific moves with a CPA or
        Enrolled Agent before selling positions large enough to matter.
      </p>
    </LearnPage>
  );
}
